import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, Form, Select, Checkbox, InputNumber, Input } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';

import { notify } from 'components/Notify/Notify';
import { markAsDamaged } from 'services/hubs';
import { getSignedUrl } from 'services/deliveries';
import { imageUpload } from 'services/upload';
import { formatImages } from 'utils/upload';
//import { rangeRule } from 'utils/forms';

import BRButton from 'components/BRButton/BRButton';
import UploadAttachment from 'components/SupportTickets/components/UploadAttachment/UploadAttachment';

import { ReactComponent as Dropdown } from 'assets/bosta-icons/Dropdown.svg';

import './DamagedModal.less';

const DamagedModal = ({ close, deliveryId, ...props }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPackingDescription, setSelectedPackingDescription] =
    useState(null);

  const intl = useIntl();
  const [form] = Form.useForm();

  const [totalItems, setTotalItems] = useState();
  const [damagedItems, setDamagedItems] = useState();

  const PACKING_DESCRIPTION_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'deliveries.damaged_modal.no_bubble_wrap'
      }),
      value: 'no_bubble_wrap'
    },
    {
      label: intl.formatMessage({ id: 'deliveries.damaged_modal.liquid' }),
      value: 'liquid'
    },
    {
      label: intl.formatMessage({ id: 'deliveries.damaged_modal.fragile' }),
      value: 'fragile'
    },
    {
      label: intl.formatMessage({
        id: 'deliveries.damaged_modal.packing_good'
      }),
      value: 'packing_good'
    }
  ];

  const increment = (fieldName) => {
    if (fieldName === 'totalItemsNumber') {
      const newVal = Math.min((totalItems || 0) + 1, 100);
      setTotalItems(newVal);
      form.setFieldsValue({ totalItemsNumber: newVal });
    } else {
      const newVal = Math.min((damagedItems || 0) + 1, 100);
      setDamagedItems(newVal);
      form.setFieldsValue({ damagedItemsNumber: newVal });
    }
  };

  const decrement = (fieldName) => {
    if (fieldName === 'totalItemsNumber') {
      const newVal = Math.max((totalItems || 1) - 1, 1);
      setTotalItems(newVal);
      form.setFieldsValue({ totalItemsNumber: newVal });
    } else {
      const newVal = Math.max((damagedItems || 1) - 1, 1);
      setDamagedItems(newVal);
      form.setFieldsValue({ damagedItemsNumber: newVal });
    }
  };

  const getImagesUrl = async (values) => {
    if (!values) {
      return [];
    }
    let imagesUrls = [];
    try {
      const formattedImages = formatImages(values);
      for (let i = 0; i < formattedImages?.length; i++) {
        const payload = {
          type: 'damaged_items_proof'
        };
        const { data } = await getSignedUrl(payload);
        await imageUpload(data?.signedUrl, formattedImages[i]);
        imagesUrls.push(data?.signedUrl);
      }
    } catch (error) {
      notify(error.message);
    }
    return imagesUrls;
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      const uploadedImages = await getImagesUrl(values.proofOfLiabilityImages);

      const totalItems = Number(values.totalItemsNumber);
      const damagedItems = Number(values.damagedItemsNumber);

      const payload = {
        deliveryId,
        packageDescription: values.packageDescription,
        totalItemsNumber: !isNaN(totalItems) ? totalItems : 0,
        damagedItemsNumber: !isNaN(damagedItems) ? damagedItems : 0,
        notes: values.notes || '',
        videoLink: values.videoLink || '',
        proofOfLiabilityImages: uploadedImages
      };

      await markAsDamaged(payload);
      notify(
        intl.formatMessage({ id: 'deliveries.damaged_modal.success' }),
        'success'
      );
      close();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'deliveries.damaged_modal.modal_header'
      })}
      footer={
        <div className="br-damaged-items__modal-footer">
          <BRButton
            className="br-damaged-items__modal-btn"
            onClick={close}
            label={intl.formatMessage({ id: 'common.cancel' })}
            disabled={isLoading}
          />
          <BRButton
            className="br-damaged-items__modal-btn"
            type="primary"
            label={intl.formatMessage({ id: 'common.confirm' })}
            form="DamagedItems"
            htmlType="submit"
            loading={isLoading}
          />
        </div>
      }
      wrapClassName="br-damaged-items__modal"
      {...props}
    >
      <Form
        name="DamagedItems"
        className="br-damaged-items__form"
        form={form}
        onFinish={onFinish}
      >
        <Form.Item
          name="packageDescription"
          label={intl.formatMessage({
            id: 'deliveries.damaged_modal.packing_description'
          })}
          //rules={[{ required: true }]}
        >
          <Select
            showArrow
            mode="multiple"
            options={PACKING_DESCRIPTION_OPTIONS}
            onChange={setSelectedPackingDescription}
          />
        </Form.Item>
        <Form.Item
          name="totalItemsNumber"
          label={intl.formatMessage({
            id: 'deliveries.damaged_modal.no_total_items'
          })}
          // rules={[
          //   { required: true },
          //   rangeRule({
          //     message: intl.formatMessage({
          //       id: 'deliveries.damaged_modal.range_required'
          //     }),
          //     min: 1,
          //     max: 30
          //   })
          // ]}
        >
          <div className="br-damaged-items__input-number-wrapper">
            <InputNumber
              controls={false}
              value={totalItems}
              onChange={(val) => {
                setTotalItems(val);
                form.setFieldsValue({ totalItemsNumber: val });
              }}
              className="br-damaged-items__input-number"
            />

            <div className="br-damaged-items__number-controls">
              <Dropdown
                className="br-damaged-items__number-arrow-up"
                onClick={() => increment('totalItemsNumber')}
              />
              <Dropdown
                className="br-damaged-items__number-arrow-down"
                onClick={() => decrement('totalItemsNumber')}
              />
            </div>
          </div>
        </Form.Item>

        <Form.Item
          name="damagedItemsNumber"
          label={intl.formatMessage({
            id: 'deliveries.damaged_modal.no_damaged_items'
          })}
          // rules={[
          //      { required: true },
          //   ({ getFieldValue }) => ({
          //     validator(_, value) {
          //       const total = Number(getFieldValue('totalItemsNumber'));
          //       const damaged = Number(value);

          //       if (!isNaN(damaged) && !isNaN(total) && damaged <= total) {
          //         return Promise.resolve();
          //       }
          //       return Promise.reject(
          //         new Error(
          //           intl.formatMessage({
          //             id: 'deliveries.damaged_modal.damage_items_error'
          //           })
          //         )
          //       );
          //     }
          //   })
          // ]}
        >
          <div className="br-damaged-items__input-number-wrapper">
            <InputNumber
              // min={0}
              // max={29}
              controls={false}
              value={damagedItems}
              onChange={(val) => {
                setDamagedItems(val);
                form.setFieldsValue({ damagedItemsNumber: val });
              }}
              className="br-damaged-items__input-number"
            />

            <div className="br-damaged-items__number-controls">
              <Dropdown
                className="br-damaged-items__number-arrow-up"
                onClick={() => increment('damagedItemsNumber')}
              />
              <Dropdown
                className="br-damaged-items__number-arrow-down"
                onClick={() => decrement('damagedItemsNumber')}
              />
            </div>
          </div>
        </Form.Item>
        <Form.Item
          label={
            <div className="br-damaged-items__form-additional-notes">
              {intl.formatMessage({
                id: 'deliveries.damaged_modal.additional_notes'
              })}
              <span className="Caption">
                {intl.formatMessage({
                  id: 'deliveries.damaged_modal.optional'
                })}
              </span>
            </div>
          }
          name="notes"
        >
          <Input.TextArea
            placeholder={intl.formatMessage({
              id: 'deliveries.damaged_modal.enter_notes'
            })}
            autoSize={{ minRows: 3, maxRows: 3 }}
          />
        </Form.Item>

        <Form.Item
          name="videoLink"
          label={
            <div className="br-damaged-items__form-additional-notes">
              {intl.formatMessage({
                id: 'deliveries.damaged_modal.google_drive'
              })}
              <span className="Caption">
                {intl.formatMessage({
                  id: 'deliveries.damaged_modal.optional'
                })}
              </span>
            </div>
          }
          rules={[
            {
              type: 'url',
              message: intl.formatMessage({
                id: 'deliveries.damaged_modal.invalid_url'
              })
            }
          ]}
        >
          <div className="br-damaged-items__form-drive-link-label">
            <Input
              placeholder={intl.formatMessage({
                id: 'deliveries.damaged_modal.placeholder'
              })}
              type="url"
            />
            <span className="Body">
              {intl.formatMessage({
                id: 'deliveries.damaged_modal.shareable'
              })}
            </span>
          </div>
        </Form.Item>

        <Form.Item
          label={intl.formatMessage({
            id: 'deliveries.damaged_modal.images'
          })}
          name="proofOfLiabilityImages"
          // rules={[
          //   {
          //     required: true,
          //     message: intl.formatMessage({
          //       id: 'deliveries.damaged_modal.images_required'
          //     })
          //   }
          // ]}
        >
          <UploadAttachment
            customSubtitle={intl.formatMessage({
              id: 'deliveries.damaged_modal.upload_text'
            })}
            maxCount={3}
            maxSize={15}
            accept=".jpg, .jpeg, .png"
            disableEncoding
            encodePreview={true}
            hideUploadButtonAfterMaxCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DamagedModal;
